import { useState } from "react";
import { Check, Plus, Home, CreditCard, TrendingUp, CheckCircle2, HelpCircle } from "lucide-react";
import { AUDIENCE_CARDS, ROADMAP, MODULES, BG_IMAGES } from "../lib/content";
import { Reveal, scrollToId } from "../lib/anim";

const ICONS = { Home, CreditCard, TrendingUp, CheckCircle2, HelpCircle };

export const AudienceFit = () => (
  <section id="learn" className="relative overflow-hidden isolate px-6 md:px-12 lg:px-16 py-28 md:py-36" data-testid="audience-section">
    <div className="absolute inset-0 -z-10 pointer-events-none" data-testid="audience-bg-image">
      <img src={BG_IMAGES.neighborhood} alt="" data-parallax="0.07" className="w-full h-full object-cover opacity-[0.16] scale-110" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #0E2A54 0%, rgba(9,30,66,0.62) 35%, rgba(9,30,66,0.62) 65%, #0E2A54 100%)" }} />
    </div>
    <Reveal>
      <p className="font-mono text-[11px] tracking-[0.3em] text-[#FFFFFF] mb-6">[003] — WHO IT'S FOR</p>
      <h2 className="font-display uppercase tracking-[-0.03em] leading-[0.95] text-[clamp(30px,4vw,54px)] text-[#D9E4F5] mb-14">
        THIS CALL IS DESIGNED FOR:
      </h2>
    </Reveal>
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-14">
      {AUDIENCE_CARDS.map((card, i) => {
        const Icon = ICONS[card.icon];
        return (
          <Reveal key={card.title} delay={i * 0.06}>
            <div
              data-testid={`audience-card-${i + 1}`}
              className="h-full p-6 border border-white/10 hover:border-[#FFFFFF] hover:bg-[#FFFFFF]/[0.08] hover:scale-[1.02] transition-[border-color,background-color,transform] duration-300"
            >
              <Icon className="w-6 h-6 text-[#FFFFFF] mb-5" />
              <p className="text-[14px] font-semibold text-[#D9E4F5] leading-snug">{card.title}</p>
            </div>
          </Reveal>
        );
      })}
    </div>
    <Reveal>
      <button
        data-testid="audience-cta-btn"
        data-cta="true"
        onClick={() => scrollToId("#register")}
        className="inline-flex items-center gap-2 bg-[#FFFFFF] text-[#0E2A54] font-bold text-sm tracking-wide px-8 py-4 hover:bg-[#DCE8FF] transition-colors duration-200"
      >
        <Check className="w-4 h-4" /> BOOK MY FREE CALL
      </button>
    </Reveal>
  </section>
);

export const Roadmap = () => (
  <section className="border-y border-white/10" data-testid="roadmap-section">
    <div className="grid md:grid-cols-4">
      {ROADMAP.map((item, i) => (
        <div
          key={item.step}
          data-testid={`roadmap-step-${i + 1}`}
          className={`relative px-8 py-16 overflow-hidden ${i < 3 ? "md:border-r border-b md:border-b-0 border-white/10" : ""}`}
        >
          <span data-parallax="0.35" className="absolute -top-4 -right-2 font-mono font-bold text-[120px] leading-none text-white/[0.04] select-none">
            {item.step}
          </span>
          <Reveal delay={i * 0.08}>
            <p className="font-mono text-[10px] tracking-[0.3em] text-[#FFFFFF] mb-4">STEP {item.step}</p>
            <h3 className="font-display uppercase text-xl text-[#D9E4F5] mb-3">{item.name}</h3>
            <p className="text-[13px] text-white/45 leading-relaxed">{item.desc}</p>
          </Reveal>
        </div>
      ))}
    </div>
  </section>
);

export const Curriculum = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="relative overflow-hidden isolate px-6 md:px-12 lg:px-16 py-28 md:py-36" data-testid="curriculum-section">
      <div className="absolute inset-y-0 right-0 w-full md:w-[58%] -z-10 pointer-events-none" data-testid="curriculum-bg-image">
        <img src={BG_IMAGES.deskPlanning} alt="" data-parallax="0.08" className="w-full h-full object-cover opacity-[0.18] scale-110" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #0E2A54 0%, rgba(9,30,66,0.55) 50%, rgba(9,30,66,0.3) 100%), linear-gradient(180deg, #0E2A54 0%, transparent 28%, transparent 72%, #0E2A54 100%)" }} />
      </div>
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-14">
          <div>
            <p className="font-mono text-[11px] tracking-[0.3em] text-[#FFFFFF] mb-6">[004] — THE CALL AGENDA</p>
            <h2 className="font-display uppercase tracking-[-0.03em] leading-[0.95] text-[clamp(30px,4vw,54px)] text-[#D9E4F5] max-w-2xl">
              WHAT WE'LL WALK THROUGH TOGETHER
            </h2>
          </div>
          <p className="font-mono text-[11px] tracking-[0.25em] text-white/40 shrink-0">4 STEPS · ONE FOCUSED CALL</p>
        </div>
      </Reveal>
      <div className="border-t border-white/10">
        {MODULES.map((m, i) => (
          <Reveal key={m.num}>
            <div className="border-b border-white/10">
              <button
                data-testid={`module-accordion-${i + 1}`}
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center gap-6 py-7 text-left group"
              >
                <span className="font-mono text-sm text-[#FFFFFF]">{m.num}</span>
                <span className="flex-1 font-display uppercase text-lg md:text-2xl tracking-[-0.02em] text-[#D9E4F5] group-hover:text-[#FFFFFF] transition-colors duration-200">
                  {m.title}
                </span>
                <Plus className={`w-5 h-5 text-[#FFFFFF] transition-transform duration-300 ${open === i ? "rotate-45" : ""}`} />
              </button>
              <div className={`overflow-hidden transition-[max-height,opacity] duration-400 ${open === i ? "max-h-40 opacity-100" : "max-h-0 opacity-0"}`}>
                <p className="pb-8 pl-12 md:pl-16 text-[15px] text-white/55 leading-relaxed max-w-2xl">{m.body}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};
